
import { useEffect, useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, AlertTriangle, Shield } from "lucide-react";
import { SecureCredentialsService } from '@/services/secureCredentialsService';
import { useAuth } from '@/contexts/AuthContext';

interface SecureConnectionStatusProps {
  onStatusChange?: (isConnected: boolean, needsRefresh: boolean) => void;
}

export const SecureConnectionStatus = ({ onStatusChange }: SecureConnectionStatusProps) => {
  const { session } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<{
    isValid: boolean;
    needsRefresh: boolean;
    error?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkConnection = async () => {
      if (!session?.user?.id) {
        setConnectionStatus({ isValid: false, needsRefresh: false, error: 'Not authenticated' });
        setIsLoading(false);
        return;
      }

      try {
        const status = await SecureCredentialsService.validateCredentials(session.user.id);
        setConnectionStatus(status);
        onStatusChange?.(status.isValid, status.needsRefresh);
      } catch (error) {
        console.error('Error checking connection status:', error);
        setConnectionStatus({ 
          isValid: false, 
          needsRefresh: false, 
          error: 'Failed to check connection status' 
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkConnection();
  }, [session, onStatusChange]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Checking connection...</span>
      </div>
    );
  }

  if (!connectionStatus) {
    return null;
  }

  const getStatusInfo = () => {
    if (connectionStatus.error) {
      return {
        icon: <XCircle className="h-4 w-4" />,
        badge: <Badge variant="destructive">Error</Badge>,
        message: connectionStatus.error,
        alertVariant: "destructive" as const
      };
    }

    if (!connectionStatus.isValid) {
      return {
        icon: <XCircle className="h-4 w-4" />,
        badge: <Badge variant="secondary">Disconnected</Badge>,
        message: "Microsoft account is not connected",
        alertVariant: "default" as const
      };
    }

    if (connectionStatus.needsRefresh) {
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        badge: <Badge variant="outline" className="border-yellow-500 text-yellow-700">Needs Refresh</Badge>,
        message: "Connection is valid but tokens will expire soon. Please reconnect to ensure continued access.",
        alertVariant: "default" as const
      };
    }

    return {
      icon: <CheckCircle className="h-4 w-4" />,
      badge: <Badge variant="default" className="bg-green-600">Connected (Secure)</Badge>,
      message: "Microsoft account is securely connected with encrypted credentials",
      alertVariant: "default" as const
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        {statusInfo.icon}
        {statusInfo.badge}
        <div className="flex items-center" title="Tokens are encrypted">
          <Shield className="h-4 w-4 text-green-600" />
        </div>
      </div>
      
      <Alert variant={statusInfo.alertVariant}>
        <AlertDescription className="text-sm">
          {statusInfo.message}
        </AlertDescription>
      </Alert>
    </div>
  );
};
